import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import GuideViewer from '../components/GuideViewer';
import { aiGeneratorGuide } from '../guides/ai-generator-guide';
import { coverGeneratorGuide } from '../guides/cover-generator-guide';
import { metadataOptimizerGuide } from '../guides/metadata-optimizer-guide';
import { 
  BookOpen, 
  Briefcase,
  Palette, 
  Target, 
  Download,
  Zap,
  CheckCircle,
  ArrowRight,
  Play,
  Star,
  Users,
  Clock,
  DollarSign,
  Eye,
  Settings,
  Code,
  FileText,
  Image,
  TrendingUp,
  BarChart3,
  Rocket
} from 'lucide-react';

const KDPGuidePage = () => {
  const [activeGuide, setActiveGuide] = useState(null);
  const [selectedGuide, setSelectedGuide] = useState(null);

  // Map guide IDs to guide objects
  const guideMap = {
    'ai-generator': aiGeneratorGuide,
    'cover-generator': coverGeneratorGuide,
    'metadata-optimizer': metadataOptimizerGuide
  };

  const handleGuideClick = (guideId) => {
    const guide = guideMap[guideId];
    if (guide) {
      setSelectedGuide(guide);
      // Scroll to top when guide is selected
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBackToGuides = () => {
    setSelectedGuide(null);
    // Scroll to the Tool Guides section instead of top of page
    setTimeout(() => {
      const toolGuidesSection = document.getElementById('tool-guides');
      if (toolGuidesSection) {
        toolGuidesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100); // Small delay to ensure DOM is updated after setSelectedGuide
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // If a guide is selected, show the guide viewer
  if (selectedGuide) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <GuideViewer guide={selectedGuide} onBack={handleBackToGuides} />
        </div>
      </div>
    );
  }

  const toolGuides = [
    {
      id: 'ai-generator',
      title: 'AI Book Generator Mastery',
      description: 'Learn how our AI creates better books than manual writing',
      icon: <Zap className="h-8 w-8" />,
      difficulty: 'Beginner',
      readTime: '8 min',
      featured: true,
      toolUsed: 'AI Book Generator'
    },
    {
      id: 'cover-generator',
      title: 'Cover Generator: KDP-Spec Designs in 60 Seconds',
      description: 'Create professional covers that convert browsers into buyers',
      icon: <Palette className="h-8 w-8" />,
      difficulty: 'Beginner',
      readTime: '5 min',
      featured: true,
      toolUsed: 'Cover Generator'
    },
    {
      id: 'metadata-optimizer',
      title: 'Why Our AI Finds Better Keywords',
      description: 'See how our metadata optimizer outperforms manual research',
      icon: <Target className="h-8 w-8" />,
      difficulty: 'Intermediate',
      readTime: '6 min',
      featured: true,
      toolUsed: 'Metadata Optimizer'
    },
    {
      id: 'book-wizard',
      title: 'Complete Book in 5 Steps',
      description: 'Master our Book Creation Wizard for fastest results',
      icon: <BookOpen className="h-8 w-8" />,
      difficulty: 'Beginner',
      readTime: '10 min',
      featured: false,
      toolUsed: 'Book Creation Wizard'
    },
    {
      id: 'multi-format',
      title: 'Download & Publishing Workflow',
      description: 'Get your books from our system to Amazon KDP',
      icon: <Download className="h-8 w-8" />,
      difficulty: 'Beginner',
      readTime: '7 min',
      featured: false,
      toolUsed: 'Multi-format Exports'
    },
    {
      id: 'end-to-end',
      title: 'Complete Publishing Workflow',
      description: 'From AI generation to Amazon bestseller using our tools',
      icon: <TrendingUp className="h-8 w-8" />,
      difficulty: 'Intermediate',
      readTime: '15 min',
      featured: false,
      toolUsed: 'Full System'
    }
  ];

  const successStories = [
    {
      name: "Sarah M.",
      result: "$3,247 in first month",
      story: "Used our AI Generator to create 3 romance novels",
      tools: ["AI Generator", "Cover Generator", "Metadata Optimizer"]
    },
    {
      name: "Mike T.",
      result: "$5,890 monthly",
      story: "Business guide series using Book Creation Wizard",
      tools: ["Book Wizard", "Cover Generator", "Multi-format Export"]
    },
    {
      name: "Lisa K.",
      result: "$1,200+ per book",
      story: "Self-help niche with optimized metadata",
      tools: ["AI Generator", "Metadata Optimizer"]
    }
  ];

  const toolShowcase = [
    {
      tool: "AI Book Generator",
      description: "See how our AI creates complete manuscripts",
      features: [
        "Any genre, any length",
        "Professional chapter structure", 
        "Engaging, readable content",
        "10x faster than manual writing"
      ],
      demoAction: "Generate Sample Chapter",
      ctaText: "Try AI Generator",
      ctaLink: "/register",
      mockup: "/guide-generator.png"
    },
    {
      tool: "Cover Generator", 
      description: "Watch KDP-spec covers created in seconds",
      features: [
        "Amazon KDP compliant",
        "Professional design templates",
        "Genre-specific styling",
        "Multiple variations for A/B testing"
      ],
      demoAction: "See Cover Examples",
      ctaText: "Create Cover",
      ctaLink: "/register",
      mockup: "/guide-cover.png"
    },
    {
      tool: "Metadata Optimizer",
      description: "Our AI finds keywords manual research misses",
      features: [
        "Competition analysis",
        "High-volume, low-competition keywords",
        "Optimized descriptions",
        "Category recommendations"
      ],
      demoAction: "View Keyword Examples",
      ctaText: "Optimize Metadata",
      ctaLink: "/register",
      mockup: "/guide-metadata.png"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      <SEOHead 
        title="Master KDP Publishing with AI Tools - Complete Guide & Tutorials"
        description="Learn how to use AI-powered tools for Amazon KDP publishing success. Step-by-step guides for book generation, cover creation, and metadata optimization. Start earning today."
        keywords="KDP guide, Amazon publishing tutorial, AI book tools, self-publishing guide, KDP tutorial, book publishing guide, Amazon KDP help, publishing workflow"
        canonical="/kdp-guide"
        ogTitle="Master KDP Publishing with AI Tools - Complete Guide"
        ogDescription="Step-by-step guides for using AI tools to create, publish, and profit from Amazon KDP books. Free tutorials and expert tips."
        ogImage="/guide-generator.png"
        ogUrl="/kdp-guide"
        schemaData={{
          "@context": "https://schema.org",
          "@type": "HowTo",
          "name": "Master KDP Publishing with AI Tools",
          "description": "Learn how to use AI-powered tools for Amazon KDP publishing success",
          "image": "https://aibookmoney.com/guide-generator.png",
          "totalTime": "PT30M",
          "supply": [
            {
              "@type": "HowToSupply",
              "name": "AI BookMoney Account"
            }
          ],
          "tool": [
            {
              "@type": "HowToTool",
              "name": "AI Book Generator"
            },
            {
              "@type": "HowToTool",
              "name": "Cover Generator"
            },
            {
              "@type": "HowToTool",
              "name": "Metadata Optimizer"
            }
          ],
          "step": [
            {
              "@type": "HowToStep",
              "name": "Generate Your Book",
              "text": "Use AI Book Generator to create professional manuscripts",
              "url": "https://aibookmoney.com/kdp-guide#ai-generator"
            },
            {
              "@type": "HowToStep",
              "name": "Design Professional Cover",
              "text": "Create KDP-compliant covers with our Cover Generator",
              "url": "https://aibookmoney.com/kdp-guide#cover-generator"
            },
            {
              "@type": "HowToStep",
              "name": "Optimize for Discovery",
              "text": "Use Metadata Optimizer for better Amazon rankings",
              "url": "https://aibookmoney.com/kdp-guide#metadata-optimizer"
            }
          ]
        }}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-16 pb-12 md:pt-20 md:pb-16 lg:pt-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Master AI BookMoney
              <br />
              <span className="bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent">
                Tools & Workflows
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Learn how to use our AI-powered tools to create, optimize, and publish profitable Amazon KDP books faster than any manual process.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/register"
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-full text-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 transform hover:scale-105"
              >
                Start Using Our Tools
              </Link>
              <Link
                to="/register"
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-full text-xl font-semibold hover:bg-white hover:text-purple-900 transition-all duration-200 flex items-center space-x-2"
              >
                <Play className="h-5 w-5" />
                <span>Start Your Free Trial</span>
              </Link>
            </div>
          </div>

          {/* Tool Usage Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="bg-purple-800/30 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20 text-center">
              <Zap className="h-12 w-12 text-green-400 mx-auto mb-4" />
              <div className="text-3xl font-bold text-white mb-2">10x Faster</div>
              <p className="text-purple-200">Book Creation vs Manual Writing</p>
            </div>
            <div className="bg-purple-800/30 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20 text-center">
              <Target className="h-12 w-12 text-green-400 mx-auto mb-4" />
              <div className="text-3xl font-bold text-white mb-2">90% Better</div>
              <p className="text-purple-200">Keyword Research vs Manual Tools</p>
            </div>
            <div className="bg-purple-800/30 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20 text-center">
              <Clock className="h-12 w-12 text-green-400 mx-auto mb-4" />
              <div className="text-3xl font-bold text-white mb-2">60 Seconds</div>
              <p className="text-purple-200">KDP-Ready Cover Generation</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tool Guides Section */}
      <section id="tool-guides" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Tool Mastery Guides
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Learn how to get maximum value from each AI BookMoney tool
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {toolGuides.filter(guide => guide.featured).map((guide, index) => (
              <div key={guide.id} className="bg-gray-900/50 rounded-2xl p-6 border border-purple-500/20 hover:border-purple-500/50 transition-all group">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg p-3 text-white">
                    {guide.icon}
                  </div>
                  <div>
                    <span className="bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full text-xs font-medium">
                      {guide.toolUsed}
                    </span>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-purple-300 transition-colors">
                  {guide.title}
                </h3>
                <p className="text-gray-300 mb-4">
                  {guide.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400 flex items-center space-x-1">
                    <Clock className="h-4 w-4" />
                    <span>{guide.readTime}</span>
                  </span>
                  <button 
                    onClick={() => handleGuideClick(guide.id)}
                    className="text-purple-400 hover:text-purple-300 font-medium flex items-center space-x-1"
                  >
                    <span>Learn Tool</span>
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* View All Guides */}
          <div className="text-center mt-12">
            <div className="bg-gray-900/50 rounded-2xl p-8 border border-purple-500/20">
              <h3 className="text-2xl font-bold text-white mb-4">More Tool Guides Coming Soon</h3>
              <p className="text-gray-300 mb-6">
                We're constantly adding new guides to help you master every aspect of AI-powered book publishing.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <BookOpen className="h-4 w-4 mr-2" />
                    Book Creation Wizard
                  </h4>
                  <p className="text-gray-400 text-sm">Complete end-to-end publishing workflow</p>
                  <span className="text-xs text-yellow-400 mt-2 block">Coming Soon</span>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Analytics Dashboard
                  </h4>
                  <p className="text-gray-400 text-sm">Track your book performance and earnings</p>
                  <span className="text-xs text-yellow-400 mt-2 block">Coming Soon</span>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2 flex items-center">
                    <Rocket className="h-4 w-4 mr-2" />
                    Marketing Automation
                  </h4>
                  <p className="text-gray-400 text-sm">Automated book promotion strategies</p>
                  <span className="text-xs text-yellow-400 mt-2 block">Coming Soon</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tool Showcase Section */}
      <section className="py-20 bg-gray-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              See Our Tools in Action
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Live demos showing exactly how our AI tools work
            </p>
          </div>

          <div className="space-y-12">
            {toolShowcase.map((tool, index) => (
              <div key={index} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12`}>
                <div className="flex-1 space-y-6">
                  <h3 className="text-3xl font-bold text-white">{tool.tool}</h3>
                  <p className="text-xl text-gray-300">
                    {tool.description}
                  </p>
                  <div className="space-y-2">
                    {tool.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <CheckCircle className="h-5 w-5 text-green-400" />
                        <span className="text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex space-x-4">
                    <Link
                      to={tool.ctaLink}
                      className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 transform hover:scale-105"
                    >
                      {tool.ctaText}
                    </Link>
                  </div>
                </div>
                <div className="flex-1">
                  {tool.mockup ? (
                    <div className="bg-gray-900 rounded-2xl overflow-hidden shadow-2xl" style={{aspectRatio: '16/10'}}>
                      <img 
                        src={tool.mockup} 
                        alt={`${tool.tool} Demo`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="bg-gradient-to-br from-purple-600 to-indigo-600 rounded-2xl p-8 h-80 flex items-center justify-center">
                      <div className="text-center text-white">
                        <div className="text-purple-200 mb-4">
                          <Zap className="h-16 w-16 mx-auto" />
                        </div>
                        <p className="text-lg opacity-80">{tool.tool} Demo</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories with Tools Used */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Success Stories: Tools in Practice
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              See how real users combine our tools for maximum profits
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {successStories.map((story, index) => (
              <div key={index} className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 rounded-xl p-6 border border-green-500/30">
                <div className="flex items-center space-x-2 mb-4">
                  <Star className="h-5 w-5 text-yellow-400 fill-current" />
                  <Star className="h-5 w-5 text-yellow-400 fill-current" />
                  <Star className="h-5 w-5 text-yellow-400 fill-current" />
                  <Star className="h-5 w-5 text-yellow-400 fill-current" />
                  <Star className="h-5 w-5 text-yellow-400 fill-current" />
                </div>
                <div className="text-2xl font-bold text-green-400 mb-2">{story.result}</div>
                <p className="text-white font-semibold mb-2">{story.name}</p>
                <p className="text-gray-300 mb-4">{story.story}</p>
                <div className="space-y-2">
                  <p className="text-sm text-gray-400">Tools Used:</p>
                  <div className="flex flex-wrap gap-1">
                    {story.tools.map((tool, idx) => (
                      <span key={idx} className="bg-purple-500/20 text-purple-300 px-2 py-1 rounded text-xs">
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-800/30 to-indigo-800/30 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Use Our Tools?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Start your free trial and see how our AI tools accelerate your publishing
          </p>
          <Link
            to="/register"
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-full text-xl font-bold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 transform hover:scale-105"
          >
            Start Free Trial - 40 Credits
          </Link>
        </div>
      </section>

      {/* Genre-Specific Guides Section - PSEO Links */}
      <section className="py-16 bg-gradient-to-r from-purple-900/20 to-indigo-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Genre-Specific Publishing Guides
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Master the unique strategies and techniques for your book's genre
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gray-800/50 rounded-xl p-6 border border-purple-500/20">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                <BookOpen className="h-5 w-5 mr-2 text-purple-400" />
                Fiction Genres
              </h3>
              <div className="space-y-2 text-sm">
                <Link to="/how-to-write-romance-books.html" className="block text-purple-300 hover:text-purple-200 transition-colors">
                  • Romance Writing & Publishing Guide
                </Link>
                <Link to="/how-to-write-fantasy-books.html" className="block text-purple-300 hover:text-purple-200 transition-colors">
                  • Fantasy World-Building Guide
                </Link>
                <Link to="/how-to-write-mystery-books.html" className="block text-purple-300 hover:text-purple-200 transition-colors">
                  • Mystery & Thriller Writing Tips
                </Link>
              </div>
            </div>

            <div className="bg-gray-800/50 rounded-xl p-6 border border-purple-500/20">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                <Briefcase className="h-5 w-5 mr-2 text-purple-400" />
                Non-Fiction Genres
              </h3>
              <div className="space-y-2 text-sm">
                <Link to="/how-to-write-business-books.html" className="block text-purple-300 hover:text-purple-200 transition-colors">
                  • Business Book Authority Guide
                </Link>
                <Link to="/how-to-write-self-help-books.html" className="block text-purple-300 hover:text-purple-200 transition-colors">
                  • Self-Help Publishing Strategy
                </Link>
                <Link to="/how-to-write-cookbook-books.html" className="block text-purple-300 hover:text-purple-200 transition-colors">
                  • Cookbook Creation & Marketing
                </Link>
              </div>
            </div>

            <div className="bg-gray-800/50 rounded-xl p-6 border border-purple-500/20">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                <Palette className="h-5 w-5 mr-2 text-purple-400" />
                Cover Design Guides
              </h3>
              <div className="space-y-2 text-sm">
                <Link to="/how-to-design-book-covers.html" className="block text-purple-300 hover:text-purple-200 transition-colors">
                  • Complete Cover Design Guide
                </Link>
                <Link to="/romance-book-cover-generator.html" className="block text-purple-300 hover:text-purple-200 transition-colors">
                  • Romance Cover Best Practices
                </Link>
                <Link to="/fantasy-book-cover-generator.html" className="block text-purple-300 hover:text-purple-200 transition-colors">
                  • Fantasy Cover Design Tips
                </Link>
              </div>
            </div>
          </div>

          <div className="text-center mt-8">
            <Link 
              to="/best-kdp-niches-2025" 
              className="inline-flex items-center bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105"
            >
              View All Profitable Niches for 2025
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Guide Viewer Modal */}
      {selectedGuide && (
        <GuideViewer 
          guide={selectedGuide} 
          onClose={() => setSelectedGuide(null)} 
        />
      )}
    </div>
  );
};

export default KDPGuidePage;